import { $streemApiV1 } from "shared/boot/api";
import DateRange from "shared/helpers/DateRange";
import type { HistogramBucket } from "shared/services/api/StreamService";
import { type Stream } from "shared/types";
import { type Mention } from "shared/types/mentions";

import {
  createEmptyIdsResponse,
  type FetchService,
  type FetchServiceGetParams,
  type FetchServiceOptions,
} from "./FetchServiceFactory";

export interface SimilarMentionsStream extends Omit<Stream, "id"> {
  id: string;
  keywords: string;
  stream_id: number;
}

interface FetchSimilarMentionsHistogramResponse {
  data: HistogramBucket[];
}

interface FetchMentionsHistogramOptions {
  range: DateRange;
  time_zone: string;
}

interface FetchSimilarMentionsHistogramParams {
  options: FetchMentionsHistogramOptions;
  stream: SimilarMentionsStream;
}

interface FetchSimilarMentions extends FetchService<
  Mention[],
  FetchServiceOptions,
  SimilarMentionsStream
> {
  histogram: (
    params: FetchSimilarMentionsHistogramParams
  ) => Promise<FetchSimilarMentionsHistogramResponse>;
}

export const fetchSimilarMentions: FetchSimilarMentions = {
  get({
    mentions,
    options = { range: DateRange.lastYear() },
    stream,
  }: FetchServiceGetParams<FetchServiceOptions, SimilarMentionsStream>) {
    const { range } = options;

    return $streemApiV1
      .get(`mentions/${stream.id}/similar_mentions`, {
        params: {
          collapse_syndicated: true,
          keywords: stream.keywords,
          ...(options.syndicationKeys
            ? { excluded_syndication_keys: options.syndicationKeys }
            : {}),
          after: range.after,
          before:
            options.keepAlreadyLoadedMentions && mentions.length
              ? mentions.slice(-1)[0].timestamp
              : range.before,
          excerpt_radius: 150,
          limit: 10,
        },
      })
      .then((response) => ({ ...response, data: response.data.mentions }));
  },

  getIds: () => Promise.resolve(createEmptyIdsResponse()),

  histogram({
    options,
    stream,
  }: {
    options: FetchMentionsHistogramOptions;
    stream: SimilarMentionsStream;
  }): Promise<FetchSimilarMentionsHistogramResponse> {
    const { range, time_zone: timeZone } = options;

    return $streemApiV1
      .get(`mentions/${stream.id}/similar_mentions`, {
        params: {
          after: range.after,
          before: range.before,
          collapse_syndicated: true,
          keywords: stream.keywords,
          limit: 0,
          time_zone: timeZone,
        },
      })
      .then(({ data, headers }) => ({ data: data.histogram, headers }));
  },
};
